// src/components/Rent/RentBeneficiaries/BeneficiariesTableView.jsx

import React, { useEffect, useMemo, useState } from 'react'
import {
    Box,
    Card,
    CardContent,
    CardHeader,
    Chip,
    Typography,
    Avatar,
    Grid,
    Divider,
    Stack,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    TextField,
    Alert,
    IconButton,
    Menu,
    ListSubheader,
    InputAdornment,
} from '@mui/material'
import { People, MoreVert, DeleteOutline, Search as SearchIcon } from '@mui/icons-material'
import AddIcon from '@mui/icons-material/Add'
import { api } from '../../../services/Main/Api'
import VisibilityIcon from '@mui/icons-material/Visibility'
import { useNavigate } from 'react-router-dom'

/* ================= Utils ================= */

const onlyDigits = (v) => String(v ?? '').replace(/\D/g, '')

const formatCPF = (value) => {
    const v = onlyDigits(value)
    if (v.length !== 11) return value ?? '-'
    return `${v.slice(0, 3)}.${v.slice(3, 6)}.${v.slice(6, 9)}-${v.slice(9, 11)}`
}

const formatCNPJ = (value) => {
    const v = onlyDigits(value)
    if (v.length !== 14) return value ?? '-'
    return `${v.slice(0, 2)}.${v.slice(2, 5)}.${v.slice(5, 8)}/${v.slice(8, 12)}-${v.slice(12, 14)}`
}

const formatCpfCnpj = (value) => {
    const v = onlyDigits(value)
    if (v.length === 11) return formatCPF(v)
    if (v.length === 14) return formatCNPJ(v)
    return value ?? '-'
}

const formatPhoneBR = (value) => {
    const v = onlyDigits(value)
    if (!v) return '-'
    if (v.length === 11) return `(${v.slice(0, 2)}) ${v.slice(2, 7)}-${v.slice(7, 11)}`
    if (v.length === 10) return `(${v.slice(0, 2)}) ${v.slice(2, 6)}-${v.slice(6, 10)}`
    return value ?? '-'
}

const clamp = (n, min, max) => Math.min(Math.max(n, min), max)

/* ================= Consts ================= */

const SIGNED_RECIPIENT_ENDPOINT = '/rent_recipient/signed_recipient/'

const PERCENTAGE_PRESETS = [
    { value: '100', label: '100%' },
    { value: '75', label: '75%' },
    { value: '50', label: '50%' },
    { value: '25', label: '25%' },
    { value: 'CUSTOM', label: 'Personalizar' },
]

/* ================= Component ================= */

const BeneficiariesTableView = ({ data = {}, onSaved }) => {
    const navigate = useNavigate()

    // fonte (props)
    const sourceItems = Array.isArray(data?.rent_recipient_data) ? data.rent_recipient_data : []

    // ✅ estado local para sumir imediatamente ao deletar
    const [itemsState, setItemsState] = useState(sourceItems)

    // sincroniza se o pai atualizar o data
    useEffect(() => {
        setItemsState(Array.isArray(data?.rent_recipient_data) ? data.rent_recipient_data : [])
    }, [data?.rent_recipient_data])

    // Select (contas disponíveis para vincular)
    const unitBeneficiaries = Array.isArray(data?.bank_accounts) ? data.bank_accounts : []

    const totalPercentage = useMemo(() => {
        return itemsState.reduce((acc, item) => acc + (Number(item?.percentage) || 0), 0)
    }, [itemsState])

    const distributionColor =
        totalPercentage === 100 ? 'success' : totalPercentage > 100 ? 'error' : 'warning'

    /* ================= Dialog Cadastro ================= */
    const [open, setOpen] = useState(false)
    const [selectedAccountId, setSelectedAccountId] = useState('')
    const [percentagePreset, setPercentagePreset] = useState('100')
    const [percentage, setPercentage] = useState('100')
    const [error, setError] = useState('')
    const [saving, setSaving] = useState(false)

    // ✅ busca dentro do select
    const [beneficiarySearch, setBeneficiarySearch] = useState('')

    // Para evitar duplicar conta já vinculada
    const usedAccountIds = useMemo(() => {
        return new Set(
            itemsState
                .map((i) => String(i?.recipient_account_id ?? i?.recipient_account ?? ''))
                .filter(Boolean)
        )
    }, [itemsState])

    const filteredUnitBeneficiaries = useMemo(() => {
        const q = String(beneficiarySearch || '').trim().toLowerCase()
        if (!q) return unitBeneficiaries

        return unitBeneficiaries.filter((b) => {
            const name = String(b?.name ?? '').toLowerCase()
            const value = String(b?.value ?? '').toLowerCase()
            const type = String(b?.account_type ?? '').toLowerCase()
            return name.includes(q) || value.includes(q) || type.includes(q)
        })
    }, [unitBeneficiaries, beneficiarySearch])

    const resetDialog = () => {
        setSelectedAccountId('')
        setPercentagePreset('100')
        setPercentage('100')
        setError('')
        setSaving(false)
        setBeneficiarySearch('')
    }

    const handleOpen = () => {
        setOpen(true)
        resetDialog()
    }

    const handleClose = () => {
        setOpen(false)
        resetDialog()
    }

    const handlePresetChange = (value) => {
        setPercentagePreset(value)
        setError('')
        if (value === 'CUSTOM') {
            setPercentage('')
            return
        }
        setPercentage(String(value))
    }

    const getRecipientIdFromAccount = (accountObj) => {
        return (
            accountObj?.recipient ||
            accountObj?.rent_recipient ||
            accountObj?.rent_recipient_id ||
            accountObj?.recipient_id ||
            null
        )
    }

    const handleSave = async () => {
        setError('')

        if (!data?.id) {
            setError('Não encontrei o ID do contrato.')
            return
        }

        if (!selectedAccountId) {
            setError('Selecione um beneficiário/conta.')
            return
        }

        const percNum = Number(String(percentage).replace(',', '.'))
        if (!Number.isFinite(percNum)) {
            setError('Informe uma porcentagem válida.')
            return
        }
        if (percNum <= 0 || percNum > 100) {
            setError('A porcentagem deve ser maior que 0 e no máximo 100.')
            return
        }

        if (usedAccountIds.has(String(selectedAccountId))) {
            setError('Esta conta já está vinculada ao contrato.')
            return
        }

        const accountObj = unitBeneficiaries.find((b) => String(b?.id) === String(selectedAccountId))
        const rentRecipientId = getRecipientIdFromAccount(accountObj)

        if (!rentRecipientId) {
            setError('Não encontrei o ID do beneficiário (rent_recipient) para a conta selecionada.')
            return
        }

        const payload = {
            rent_contract: data.id,
            rent_recipient: rentRecipientId,
            recipient_account: selectedAccountId,
            percentage: percNum,
        }

        try {
            setSaving(true)
            await api.post(SIGNED_RECIPIENT_ENDPOINT, payload)
            handleClose()
            if (typeof onSaved === 'function') onSaved(null, true)
        } catch (e) {
            const msg =
                e?.response?.data?.rent_recipient ||
                e?.response?.data?.percentage ||
                e?.response?.data?.detail ||
                'Não foi possível salvar o beneficiário.'
            setError(msg)
        } finally {
            setSaving(false)
        }
    }

    /* ================= Menu / Delete / Details ================= */
    const [menuAnchor, setMenuAnchor] = useState(null)
    const [selectedItem, setSelectedItem] = useState(null)
    const [confirmDelete, setConfirmDelete] = useState(false)
    const [deleting, setDeleting] = useState(false)

    const openMenu = Boolean(menuAnchor)

    const handleMenuOpen = (event, item) => {
        setMenuAnchor(event.currentTarget)
        setSelectedItem(item)
    }

    const handleMenuClose = () => setMenuAnchor(null)

    const handleAskDelete = () => {
        handleMenuClose()
        setConfirmDelete(true)
    }

    const getBeneficiaryIdFromItem = (it) => {
        // ✅ tente pegar o ID real do beneficiário (RentRecipient) primeiro
        return (
            it?.rent_recipient_id ??
            it?.rent_recipient ??
            it?.recipient_id ??
            it?.recipient ??
            it?.rent_recipient?.id ??
            it?.recipient?.id ??
            null
        )
    }

    const handleGoToBeneficiary = () => {
        const beneficiaryId = getBeneficiaryIdFromItem(selectedItem)

        handleMenuClose()

        if (!beneficiaryId) {
            console.warn('Não encontrei o id do beneficiário para navegar.')
            return
        }

        // ✅ ajuste a rota conforme seu app
        navigate(`/beneficiarios/${beneficiaryId}`)
    }

    const handleDelete = async () => {
        if (!selectedItem?.id) return

        try {
            setDeleting(true)
            await api.delete(`${SIGNED_RECIPIENT_ENDPOINT}${selectedItem.id}/`)

            // some na hora
            setItemsState((prev) => prev.filter((it) => it.id !== selectedItem.id))

            setConfirmDelete(false)
            setSelectedItem(null)

            if (typeof onSaved === 'function') onSaved()
        } catch (e) {
            console.error(e)
        } finally {
            setDeleting(false)
        }
    }

    return (
        <>
            {/* HEADER PRINCIPAL */}
            <CardHeader
                avatar={
                    <Avatar sx={{ bgcolor: 'primary.main' }}>
                        <People />
                    </Avatar>
                }
                title="Beneficiários"
                action={
                    <Box display="flex" alignItems="center" gap={2}>
                        <Chip
                            label={`Distribuição: ${totalPercentage || 0}%`}
                            color={distributionColor}
                            variant="outlined"
                            size="small"
                        />
                        <Button
                            size="small"
                            startIcon={<AddIcon />}
                            variant="contained"
                            onClick={handleOpen}
                            disabled={totalPercentage >= 100}
                        >
                            Novo Beneficiário
                        </Button>
                    </Box>
                }
                sx={{ '& .MuiCardHeader-subheader': { whiteSpace: 'normal' } }}
            />

            {/* Dialog de cadastro */}
            <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
                <DialogTitle>Novo beneficiário</DialogTitle>

                <DialogContent dividers>
                    <Stack spacing={2}>
                        <Box>{error && <Alert sx={{ mb: '0.5rem' }} severity="error">{error}</Alert>}</Box>

                        <FormControl fullWidth size="small">
                            <InputLabel id="beneficiary-select-label">Beneficiário</InputLabel>
                            <Select
                                labelId="beneficiary-select-label"
                                value={selectedAccountId}
                                label="Beneficiário"
                                onChange={(e) => setSelectedAccountId(e.target.value)}
                                MenuProps={{
                                    PaperProps: { sx: { maxHeight: 420 } },
                                }}
                            >
                                {/* ✅ Campo de busca dentro do menu */}
                                <ListSubheader
                                    sx={{
                                        bgcolor: 'background.paper',
                                        position: 'sticky',
                                        top: 0,
                                        zIndex: 1,
                                        pb: 1,
                                    }}
                                >
                                    <TextField
                                        autoFocus
                                        size="small"
                                        placeholder="Buscar beneficiário..."
                                        fullWidth
                                        value={beneficiarySearch}
                                        onChange={(e) => setBeneficiarySearch(e.target.value)}
                                        onKeyDown={(e) => {
                                            // evita o Select “capturar” teclas e fechar/selecionar
                                            e.stopPropagation()
                                        }}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <SearchIcon fontSize="small" />
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                </ListSubheader>

                                {unitBeneficiaries.length === 0 ? (
                                    <MenuItem value="" disabled>
                                        Nenhum beneficiário disponível para este imóvel
                                    </MenuItem>
                                ) : filteredUnitBeneficiaries.length === 0 ? (
                                    <MenuItem value="" disabled>
                                        Nenhum resultado para “{beneficiarySearch}”
                                    </MenuItem>
                                ) : (
                                    filteredUnitBeneficiaries.map((b) => {
                                        const disabled = usedAccountIds.has(String(b.id))
                                        return (
                                            <MenuItem key={b.id} value={String(b.id)} disabled={disabled}>
                                                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                                    <Typography variant="body2" fontWeight={600}>
                                                        {b?.name ?? '-'}
                                                        {b?.account_type ? ` • ${b.account_type}` : ''}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {b?.value ?? '-'}
                                                    </Typography>
                                                </Box>
                                            </MenuItem>
                                        )
                                    })
                                )}
                            </Select>
                        </FormControl>

                        <FormControl fullWidth size="small">
                            <InputLabel id="percentage-preset-label">Porcentagem</InputLabel>
                            <Select
                                labelId="percentage-preset-label"
                                value={percentagePreset}
                                label="Porcentagem"
                                onChange={(e) => handlePresetChange(e.target.value)}
                            >
                                {PERCENTAGE_PRESETS.map((opt) => (
                                    <MenuItem key={opt.value} value={opt.value}>
                                        {opt.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        {percentagePreset === 'CUSTOM' && (
                            <TextField
                                label="Porcentagem personalizada"
                                size="small"
                                fullWidth
                                value={percentage}
                                onChange={(e) => {
                                    const raw = e.target.value
                                    const cleaned = raw.replace(/[^\d.,]/g, '')
                                    setPercentage(cleaned)
                                }}
                                onBlur={() => {
                                    const n = Number(String(percentage).replace(',', '.'))
                                    if (Number.isFinite(n)) setPercentage(String(clamp(n, 0, 100)).replace('.', ','))
                                }}
                                helperText="Informe um valor entre 0 e 100"
                                inputProps={{ inputMode: 'decimal' }}
                            />
                        )}
                    </Stack>
                </DialogContent>

                <DialogActions>
                    <Button onClick={handleClose} disabled={saving}>
                        Cancelar
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleSave}
                        disabled={unitBeneficiaries.length === 0 || saving}
                    >
                        {saving ? 'Salvando...' : 'Salvar'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* LISTAGEM */}
            {itemsState.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                    Nenhum beneficiário cadastrado.
                </Typography>
            ) : (
                <Stack spacing={2}>
                    {itemsState.map((item) => (
                        <Card key={item.id ?? item.email ?? item.name}>
                            <CardHeader
                                title={item.name || '-'}
                                action={
                                    <Box display="flex" alignItems="center" gap={1}>
                                        <Chip
                                            label={`${Number(item?.percentage) || 0}%`}
                                            variant="outlined"
                                            color="success"
                                            size="small"
                                        />
                                        <IconButton size="small" onClick={(e) => handleMenuOpen(e, item)}>
                                            <MoreVert fontSize="small" />
                                        </IconButton>
                                    </Box>
                                }
                            />
                            <Divider />
                            <CardContent>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} md={3}>
                                        <Typography variant="body2" color="text.secondary">
                                            CPF/CNPJ
                                        </Typography>
                                        <Typography variant="body1">{formatCpfCnpj(item?.cpf_cnpj)}</Typography>
                                    </Grid>

                                    <Grid item xs={12} md={3}>
                                        <Typography variant="body2" color="text.secondary">
                                            E-mail
                                        </Typography>
                                        <Typography variant="body1">{item?.email || '-'}</Typography>
                                    </Grid>

                                    <Grid item xs={12} md={3}>
                                        <Typography variant="body2" color="text.secondary">
                                            Telefone
                                        </Typography>
                                        <Typography variant="body1">{formatPhoneBR(item?.phone)}</Typography>
                                    </Grid>

                                    <Grid item xs={12} md={3}>
                                        <Typography variant="body2" color="text.secondary">
                                            Conta {item?.bank_account_type ?? '-'}
                                        </Typography>
                                        <Typography variant="body1">{item?.bank_account_info ?? '-'}</Typography>
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>
                    ))}
                </Stack>
            )}

            {/* MENU ⋮ */}
            <Menu anchorEl={menuAnchor} open={openMenu} onClose={handleMenuClose}>
                <MenuItem onClick={handleGoToBeneficiary}>
                    <VisibilityIcon fontSize="small" style={{ marginRight: 8 }} />
                    Detalhes
                </MenuItem>
                <Divider />
                <MenuItem onClick={handleAskDelete}>
                    <DeleteOutline color="error" fontSize="small" style={{ marginRight: 8 }} />
                    <Typography color="error">Remover</Typography>
                </MenuItem>
            </Menu>

            {/* CONFIRMAÇÃO DELETE */}
            <Dialog open={confirmDelete} onClose={() => setConfirmDelete(false)}>
                <DialogTitle>Remover beneficiário</DialogTitle>
                <DialogContent>
                    <Typography>
                        Tem certeza que deseja remover{' '}
                        <strong>{selectedItem?.name ?? 'este beneficiário'}</strong>?
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmDelete(false)} disabled={deleting}>
                        Cancelar
                    </Button>
                    <Button color="error" variant="contained" onClick={handleDelete} disabled={deleting}>
                        {deleting ? 'Removendo...' : 'Remover'}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    )
}

export default BeneficiariesTableView
